/* ------------------------------------------------------------
 * Calculators hub
 *
 * Handles category filtering and the tool modal. Each tool has a
 * permanent mount node in the DOM (`[data-tool-mount="<id>"]`), so a
 * React root mounted into it survives open/close — opening only
 * toggles panel visibility.
 *
 * ---- Integrating a React tool -------------------------------
 *
 * Mount eagerly on load:
 *
 *   const node = document.querySelector('[data-tool-mount="roas"]');
 *   createRoot(node).render(<Roas />);
 *   window.TDMCCalculators.markMounted('roas');
 *
 * Or lazily, on first open:
 *
 *   document.addEventListener('calculator:open', async (event) => {
 *     const { id, mount } = event.detail;
 *     if (mount.dataset.mounted) return;
 *     const { default: Tool } = await import(`./tools/${id}.js`);
 *     createRoot(mount).render(<Tool />);
 *     window.TDMCCalculators.markMounted(id);
 *   });
 *
 * `calculator:close` fires with the same detail shape when a tool is
 * closed — use it to reset transient state if a tool needs it.
 * ------------------------------------------------------------ */

(function () {
	'use strict';

	var FOCUSABLE = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])'
	].join(',');

	function CalculatorHub(root) {
		this.root = root;
		this.modal = root.querySelector('[data-calc-modal]');
		this.dialog = this.modal ? this.modal.querySelector('.calc-modal__dialog') : null;
		this.titleEl = root.querySelector('[data-calc-modal-title]');
		this.categoryEl = root.querySelector('[data-calc-modal-category]');
		this.visibleCountEl = root.querySelector('[data-calc-visible-count]');
		this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-calc-open]'));
		this.categories = Array.prototype.slice.call(root.querySelectorAll('[data-calc-category]'));
		this.filters = Array.prototype.slice.call(root.querySelectorAll('[data-calc-filter]'));
		this.deepLink = root.dataset.openOnHash === 'true';
		this.activeId = null;
		this.lastFocused = null;

		this.onKeydown = this.onKeydown.bind(this);
		this.onHashChange = this.onHashChange.bind(this);

		this.bind();
		this.restoreFromHash();
	}

	CalculatorHub.prototype.bind = function () {
		var self = this;

		this.cards.forEach(function (card) {
			card.addEventListener('click', function () {
				self.open(card.dataset.calcOpen);
			});
		});

		this.filters.forEach(function (filter) {
			filter.addEventListener('click', function () {
				self.filter(filter.dataset.calcFilter);
			});
		});

		if (this.modal) {
			this.modal.querySelectorAll('[data-calc-close]').forEach(function (el) {
				el.addEventListener('click', function () {
					self.close();
				});
			});
		}

		if (this.deepLink) {
			window.addEventListener('hashchange', this.onHashChange);
		}
	};

	/* ---------- Filtering ---------- */

	CalculatorHub.prototype.filter = function (slug) {
		var visible = 0;

		this.filters.forEach(function (filter) {
			filter.setAttribute('aria-pressed', String(filter.dataset.calcFilter === slug));
		});

		this.categories.forEach(function (category) {
			var match = slug === 'all' || category.dataset.calcCategory === slug;
			category.hidden = !match;
			if (match) {
				visible += category.querySelectorAll('[data-calc-open]').length;
			}
		});

		this.root.classList.toggle('calc-hub--no-results', visible === 0);

		if (this.visibleCountEl) {
			this.visibleCountEl.textContent = String(visible);
		}
	};

	/* ---------- Modal ---------- */

	CalculatorHub.prototype.open = function (id) {
		if (!this.modal || !id) return;

		var panel = this.modal.querySelector('[data-calc-panel="' + CSS.escape(id) + '"]');
		if (!panel) return;

		var card = this.root.querySelector('[data-calc-open="' + CSS.escape(id) + '"]');

		this.lastFocused = document.activeElement;
		this.activeId = id;

		this.modal.querySelectorAll('[data-calc-panel]').forEach(function (el) {
			el.hidden = el !== panel;
		});

		if (card) {
			var titleEl = card.querySelector('.calc-card__title');
			var categoryEl = card.closest('[data-calc-category]');
			var categoryTitle = categoryEl ? categoryEl.querySelector('.calc-category__title') : null;

			if (this.titleEl && titleEl) {
				this.titleEl.textContent = titleEl.textContent.trim();
			}
			if (this.categoryEl) {
				this.categoryEl.textContent = categoryTitle ? categoryTitle.textContent.trim() : '';
			}
		}

		this.modal.hidden = false;
		this.lockScroll(true);

		// Force a reflow so the enter transition runs from its start state.
		void this.modal.offsetHeight;
		this.modal.classList.add('is-open');

		document.addEventListener('keydown', this.onKeydown);

		var mount = panel.querySelector('[data-tool-mount]');
		this.focusFirst(panel);
		loadCalculatorBundle();

		if (this.deepLink) {
			this.setHash('tool-' + id);
		}

		document.dispatchEvent(
			new CustomEvent('calculator:open', {
				bubbles: true,
				detail: { id: id, mount: mount, panel: panel, hub: this.root }
			})
		);
	};

	CalculatorHub.prototype.close = function () {
		if (!this.modal || this.modal.hidden) return;

		var self = this;
		var id = this.activeId;
		var panel = id ? this.modal.querySelector('[data-calc-panel="' + CSS.escape(id) + '"]') : null;

		this.modal.classList.remove('is-open');
		document.removeEventListener('keydown', this.onKeydown);
		this.lockScroll(false);

		var finish = function () {
			self.modal.hidden = true;
		};

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			finish();
		} else {
			window.setTimeout(finish, 220);
		}

		if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
			this.lastFocused.focus();
		}

		if (this.deepLink && window.location.hash === '#tool-' + id) {
			this.setHash('');
		}

		this.activeId = null;

		document.dispatchEvent(
			new CustomEvent('calculator:close', {
				bubbles: true,
				detail: {
					id: id,
					mount: panel ? panel.querySelector('[data-tool-mount]') : null,
					panel: panel,
					hub: this.root
				}
			})
		);
	};

	/* ---------- Calculator bundle ----------
	 *
	 * The React bundle is ~700KB, so it is not shipped with the page. It is
	 * injected the first time any tool is opened; once loaded it drains the
	 * currently-open panel and then handles `calculator:open` itself.
	 */

	var bundleRequested = false;

	function loadCalculatorBundle() {
		if (bundleRequested) return;
		var assets = window.TDMC_CALC_ASSETS;
		if (!assets || !assets.js) return;
		bundleRequested = true;

		if (assets.css) {
			var link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = assets.css;
			document.head.appendChild(link);
		}

		var script = document.createElement('script');
		script.src = assets.js;
		script.async = true;
		script.onerror = function () {
			bundleRequested = false;
			console.error('[calculators] Failed to load tool bundle from', assets.js);
		};
		document.head.appendChild(script);
	}

	/* ---------- Scroll lock ----------
	 *
	 * The theme runs Lenis on <html>, so `body { overflow: hidden }` alone
	 * leaves the page scrolling behind the modal. Stop Lenis as well, and
	 * mark the modal body with `data-lenis-prevent` so it still scrolls.
	 */

	CalculatorHub.prototype.lockScroll = function (locked) {
		document.documentElement.classList.toggle('calc-modal-open', locked);
		document.body.classList.toggle('calc-modal-open', locked);

		var lenis = window.lenis;
		if (!lenis) return;

		if (locked && typeof lenis.stop === 'function') {
			lenis.stop();
		} else if (!locked && typeof lenis.start === 'function') {
			lenis.start();
		}
	};

	/* ---------- Focus management ---------- */

	CalculatorHub.prototype.focusFirst = function (panel) {
		var target = panel.querySelector(FOCUSABLE) || this.modal.querySelector('.calc-modal__close');
		if (target) {
			target.focus();
		}
	};

	CalculatorHub.prototype.onKeydown = function (event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			return;
		}

		if (event.key !== 'Tab' || !this.dialog) return;

		var focusable = Array.prototype.slice
			.call(this.dialog.querySelectorAll(FOCUSABLE))
			.filter(function (el) {
				return el.offsetParent !== null || el === document.activeElement;
			});

		if (focusable.length === 0) return;

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	/* ---------- Deep links ---------- */

	CalculatorHub.prototype.setHash = function (hash) {
		var url = window.location.pathname + window.location.search + (hash ? '#' + hash : '');
		window.history.replaceState(null, '', url);
	};

	CalculatorHub.prototype.restoreFromHash = function () {
		if (!this.deepLink) return;
		var match = /^#tool-(.+)$/.exec(window.location.hash);
		if (match) {
			this.open(decodeURIComponent(match[1]));
		}
	};

	CalculatorHub.prototype.onHashChange = function () {
		var match = /^#tool-(.+)$/.exec(window.location.hash);
		if (match) {
			var id = decodeURIComponent(match[1]);
			if (id !== this.activeId) {
				this.open(id);
			}
		} else if (this.activeId) {
			this.close();
		}
	};

	/* ---------- Public API ---------- */

	var api = {
		instances: [],

		get: function (index) {
			return this.instances[index || 0];
		},

		open: function (id) {
			var instance = this.instances.find(function (hub) {
				return hub.root.querySelector('[data-calc-panel="' + CSS.escape(id) + '"]');
			});
			if (instance) instance.open(id);
		},

		close: function () {
			this.instances.forEach(function (hub) {
				hub.close();
			});
		},

		/* Call once a React root has rendered into the mount node — hides the placeholder. */
		markMounted: function (id) {
			document.querySelectorAll('[data-tool-mount="' + CSS.escape(id) + '"]').forEach(function (mount) {
				mount.dataset.mounted = 'true';
				mount.classList.add('is-mounted');
			});
		}
	};

	function init(scope) {
		(scope || document).querySelectorAll('[data-calc-hub]').forEach(function (root) {
			if (root.dataset.calcInit === 'true') return;
			root.dataset.calcInit = 'true';
			api.instances.push(new CalculatorHub(root));
		});
	}

	window.TDMCCalculators = api;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			init();
		});
	} else {
		init();
	}

	/* Theme editor: re-init when the section is re-rendered. */
	document.addEventListener('shopify:section:load', function (event) {
		api.instances = api.instances.filter(function (hub) {
			return document.contains(hub.root);
		});
		init(event.target);
	});

	document.addEventListener('shopify:section:unload', function () {
		document.documentElement.classList.remove('calc-modal-open');
		document.body.classList.remove('calc-modal-open');
		if (window.lenis && typeof window.lenis.start === 'function') {
			window.lenis.start();
		}
		api.instances = api.instances.filter(function (hub) {
			return document.contains(hub.root);
		});
	});
})();
